import React, { useState, useEffect, useRef } from 'react';
import { dashboardAPI, leadsAPI } from '../services/api';
import FilterBar from './FilterBar';
import { ExternalLink, Trash2, Edit, Upload, X, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toViewableUrl } from '../utils/fileLinks';


const OnboardedClients = ({ onNavigateToPreSales, onExportExcel, onExportPDF, showExport = false }) => {
  const { isAdmin } = useAuth();
  
  const [onboardedClients, setOnboardedClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBDE, setSelectedBDE] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [, setBdeUsers] = useState([]);
  const [editingClient, setEditingClient] = useState(null);
  const [editingDate, setEditingDate] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const hasLoadedRef = useRef(false);

  // Helper function to normalize URLs
  const normalizeUrl = (url) => {
    if (!url) return '';
    const trimmed = url.trim();
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed.replace(/^https?:\/\//i, trimmed.toLowerCase().startsWith('https') ? 'https://' : 'http://');
    }
    return 'https://' + trimmed;
  };

  useEffect(() => {
    fetchBDEUsers();
  }, []);

  useEffect(() => {
    fetchOnboardedClients();
    hasLoadedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBDE, searchTerm, dateFrom, dateTo]);
  
  // Initial load and also refresh when component becomes visible
  useEffect(() => {
    fetchOnboardedClients();
    hasLoadedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBDEUsers = async () => {
    try {
      const result = await dashboardAPI.getBDEUsers();
      if (result.success) {
        setBdeUsers(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching BDE users:', error);
    }
  };

  const fetchOnboardedClients = async () => {
    try {
      if (!hasLoadedRef.current) setLoading(true);
      const params = {
        bde: selectedBDE !== 'All' ? selectedBDE : undefined,
        search: searchTerm || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined
      };
      
      console.log('🔄 Fetching onboarded clients with params:', params);
      const result = await dashboardAPI.getOnboardedClients(params);
      console.log('✅ Onboarded clients result:', result);
      if (result.success) {
        console.log('📊 Onboarded clients data:', result.data);
        setOnboardedClients(result.data || []);
      } else {
        console.error('❌ Failed to fetch onboarded clients:', result.error);
      }
    } catch (error) {
      console.error('❌ Error fetching onboarded clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOnboardingDate = async (id, newDate) => {
    const dateStr = (newDate || '').trim();
    if (!dateStr) {
      alert('Please select a date.');
      return;
    }
    try {
      setLoading(true);
      const result = await dashboardAPI.updateOnboardedClient(id, { onboarding_date: dateStr });
      if (result.success) {
        setEditingClient(null);
        setEditingDate('');
        fetchOnboardedClients();
      } else {
        alert('Error updating onboarding date: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating onboarding date:', error);
      console.error('Response:', error.response?.data);
      const msg = error.response?.data?.error
        || (error.response ? `Server error (${error.response.status})` : null)
        || (error.code === 'ECONNABORTED' ? 'Request timed out.' : null)
        || error.message;
      alert('Error updating onboarding date: ' + (msg || 'Backend unreachable. Redeploy the backend and set REACT_APP_API_URL.'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOnboardedClient = async (id, companyName) => {
    if (window.confirm(`Are you sure you want to move "${companyName}" back to Potential Clients?`)) {
      try {
        setLoading(true);
        const result = await dashboardAPI.deleteOnboardedClient(id);
        console.log('🔄 Delete onboarded client result:', result);
        if (result.success) {
          // No alert, just refresh
          fetchOnboardedClients();
          // Refresh potential clients if that component is available
          if (window.potentialClientsRefresh) {
            console.log('🔄 Refreshing potential clients list');
            window.potentialClientsRefresh();
          } else {
            console.warn('⚠️ potentialClientsRefresh function not available');
          }
        } else {
          console.error('❌ Error moving client:', result.error);
          alert('Error moving client: ' + (result.error || 'Unknown error'));
        }
      } catch (error) {
        console.error('Error moving onboarded client:', error);
        alert('Error moving client. Please check if backend is running.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleUploadDocument = async (clientId, file) => {
    try {
      setUploadingDoc(true);
      const result = await dashboardAPI.uploadOnboardedClientDocument(clientId, file);
      if (result.success) {
        // No alert, just refresh
        fetchOnboardedClients();
      } else {
        alert('Error uploading document: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Error uploading document. Please check if backend is running.');
    } finally {
      setUploadingDoc(false);
    }
  };

  const getDocuments = (client) => {
    if (!client.important_documents) return [];
    try {
      return typeof client.important_documents === 'string' 
        ? JSON.parse(client.important_documents)
        : client.important_documents;
    } catch (e) {
      return [];
    }
  };

  // Expose refresh function globally
  useEffect(() => {
    window.onboardedClientsRefresh = fetchOnboardedClients;
    // Expose filter values for export
    window.onboardedClientsFilters = {
      selectedBDE,
      searchTerm,
      dateFrom,
      dateTo
    };
    return () => {
      delete window.onboardedClientsRefresh;
      delete window.onboardedClientsFilters;
    };
  }, [selectedBDE, searchTerm, dateFrom, dateTo]);

  return (
    <div className="space-y-4">
      <FilterBar
        selectedDate=""
        setSelectedDate={() => {}}
        dateFrom={dateFrom}
        setDateFrom={setDateFrom}
        dateTo={dateTo}
        setDateTo={setDateTo}
        selectedBDE={selectedBDE}
        setSelectedBDE={setSelectedBDE}
        selectedStage="All"
        setSelectedStage={() => {}}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        showDateRange={true}
        showSearch={true}
        showStageFilter={false}
        onExportExcel={onExportExcel}
        onExportPDF={onExportPDF}
        showExport={showExport}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Onboarded Clients ({onboardedClients.length})</h2>
      </div>

      {loading ? (
        <div className="text-center py-10">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#679632]" />
          <p className="mt-2 text-gray-500 text-sm">Loading...</p>
        </div>
      ) : onboardedClients.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-400 text-sm">No onboarded clients found</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">Company</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">POC</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">Location</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">Docs</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">BDE</th>
                  {isAdmin && (
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {onboardedClients.map((client) => {
                  const documents = getDocuments(client);
                  const rowClick = onNavigateToPreSales && client.source_lead_id ? async (e) => {
                    if (e.target.closest('button') || e.target.closest('a') || e.target.closest('input')) return;
                    try { const r = await leadsAPI.getById(client.source_lead_id); if (r.data) onNavigateToPreSales(r.data); }
                    catch (err) { console.error('Error fetching lead:', err); }
                  } : undefined;

                  return (
                    <tr key={client.id} className={`hover:bg-gray-50 ${rowClick ? 'cursor-pointer' : ''}`} onClick={rowClick}>
                      <td className="px-4 py-2.5 text-sm text-gray-600 whitespace-nowrap">
                        {editingClient === client.id ? (
                          <div className="flex items-center gap-1">
                            <input type="date" value={editingDate} onChange={(e) => setEditingDate(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-xs" />
                            <button onClick={() => handleUpdateOnboardingDate(client.id, editingDate)} className="text-green-600 hover:text-green-800"><CheckCircle size={14} /></button>
                            <button onClick={() => { setEditingClient(null); setEditingDate(''); }} className="text-red-500 hover:text-red-700"><X size={14} /></button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <span>{client.onboarding_date ? (() => { const [y,m,d] = client.onboarding_date.split('-').map(Number); return new Date(y,m-1,d).toLocaleDateString('en-US',{month:'2-digit',day:'2-digit',year:'numeric'}); })() : '-'}</span>
                            <button onClick={(e) => { e.stopPropagation(); setEditingClient(client.id); setEditingDate(client.onboarding_date || ''); }} className="text-gray-400 hover:text-blue-600"><Edit size={12} /></button>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <span className={`text-sm font-medium ${rowClick ? 'text-[#679632] hover:underline' : 'text-gray-900'}`}>{client.company_name}</span>
                          {client.company_url && <a href={normalizeUrl(client.company_url)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-gray-400 hover:text-blue-600" title="Website"><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg></a>}
                          {client.linkedin_url && <a href={normalizeUrl(client.linkedin_url)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-gray-400 hover:text-[#0077b5]" title="LinkedIn"><svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg></a>}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <p className="text-sm text-gray-900">{client.poc_name || '-'}</p>
                        {client.poc_email && <p className="text-xs text-gray-500 truncate max-w-[180px]">{client.poc_email}</p>}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-gray-600">{[client.city, client.country].filter(Boolean).join(', ') || '-'}</td>
                      <td className="px-4 py-2.5 text-sm">
                        {documents.length > 0 ? documents.map((doc, idx) => (
                          <a key={idx} href={toViewableUrl(doc.url)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-0.5">
                            <ExternalLink size={11} /> {doc.name || `Doc ${idx+1}`}
                          </a>
                        )) : <span className="text-gray-400 text-xs">-</span>}
                          {isAdmin && client.id && (
                            <label className="flex items-center gap-0.5 text-[10px] text-[#679632] hover:text-[#587f2b] cursor-pointer mt-0.5">
                              <Upload size={10} />
                              <input type="file" accept=".pdf,.doc,.docx,.txt" className="hidden" onChange={(e) => { if (e.target.files?.[0]) handleUploadDocument(client.id, e.target.files[0]); }} disabled={uploadingDoc} />
                              {uploadingDoc ? '...' : '+ Add'}
                            </label>
                          )}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-gray-600">{client.bde_name || '-'}</td>
                      {isAdmin && (
                        <td className="px-4 py-2.5 text-right">
                          <button onClick={(e) => { e.stopPropagation(); handleDeleteOnboardedClient(client.id, client.company_name); }}
                            className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                            <Trash2 size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardedClients;

